import { useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import Forbidden from '../Forbidden';
import { dspLabel } from '../ReportingV2/format';

type FreshnessStatus = 'current' | 'late' | 'missing' | 'no_data';

interface PartnerPlatformFreshness {
  dsp: string;
  lastDate: string | null;
  status: FreshnessStatus;
}

interface PartnerFreshnessResult {
  whitelabelId: number;
  whitelabelName: string;
  platforms: PartnerPlatformFreshness[];
}

// Matches the labels/colors a reader can scan at a glance — green means
// "don't look here," everything else is in increasing order of concern.
// Kept local rather than importing a shared status-chip component: this is
// the only screen with this exact four-state vocabulary (flank#4463).
const STATUS_META: Record<FreshnessStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  current: { label: 'Current', color: 'success' },
  late: { label: 'Late', color: 'warning' },
  missing: { label: 'Missing', color: 'error' },
  no_data: { label: 'No data', color: 'default' },
};

const PartnerDataHealth = () => {
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const [rows, setRows] = useState<PartnerFreshnessResult[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    axios
      .get<{ data: PartnerFreshnessResult[] }>('/reporting-2.0/partner-data-health')
      .then(response => {
        if (!cancelled) setRows(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  // Belt-and-suspenders with the backend's superAdminOnly route middleware
  // (flank#4463) — this check alone would never be the real gate.
  if (!isSuperAdmin) return <Forbidden />;

  const dsps = [...new Set(rows?.flatMap(r => r.platforms.map(p => p.dsp)) ?? [])].sort();

  return (
    <div className='w-full p-[30px]'>
      <Typography variant='h5' sx={{ mb: 1 }}>
        Partner Data Health
      </Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        Latest date with delivery, per partner and platform. A gap of 2-3 days reads as <strong>Late</strong> (still
        plausibly normal lag); past that, <strong>Missing</strong>. This threshold isn't yet confirmed by product — flag
        if it should move.
      </Typography>

      {error && <Typography color='error'>Couldn&apos;t load partner data health. Try reloading.</Typography>}

      {!error && !rows && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!error && rows && rows.length === 0 && <Typography>No partners found.</Typography>}

      {!error && rows && rows.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Partner</TableCell>
              {dsps.map(dsp => (
                <TableCell key={dsp} align='center'>
                  {dspLabel(dsp)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const byDsp = new Map(row.platforms.map(p => [p.dsp, p]));
              return (
                <TableRow key={row.whitelabelId}>
                  <TableCell>{row.whitelabelName}</TableCell>
                  {dsps.map(dsp => {
                    const platform = byDsp.get(dsp);
                    const status = platform?.status ?? 'no_data';
                    const meta = STATUS_META[status];
                    return (
                      <TableCell key={dsp} align='center'>
                        <Chip label={meta.label} color={meta.color} size='small' sx={{ mb: 0.5 }} />
                        {platform?.lastDate && (
                          <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                            {platform.lastDate}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default PartnerDataHealth;
